import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const areas = [
  "TOP HEADER",
  "BOX COLUMN",
  "SERVICE TABLE HEADER",
  "SERVICE TABLE BODY",
  "SERVICE TABLE FOOTER",
  "BANK DETAILS HEADER",
  "BANK DETAILS BODY",
];

const fonts = ["Verdana", "Arial", "Times New Roman", "Roboto", "Inter", "Open Sans"];

const TemplateSettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    is_active: false,
    title: "TAX INVOICE",
    defaultFont: "Verdana",
    defaultFontColor: "#0b2c4d",
    defaultFontSize: 12,
    headerText: "Dear Valued Customer,\n\nGreetings from Rekonic Travel & Tourism.",
    detailsText1: "THANK YOU for taking service from us.\n\nPlease find below invoice amount for the services.",
    detailsText2: "We care for our valued customers and are always available to attend to your queries.",
    termsAndConditions: "This is a computer generated document and does not require any official signature.",
    addStamp: true,
    addSignature: true,
    addBankDetails: true,
    logoUrl: "",
    stampUrl: "",
    signatureUrl: "",
  });

  const [areaStyles, setAreaStyles] = useState(
    areas.map((area) => ({
      area,
      bgColor: "#ffffff",
      fontColor: "#000000",
      fontSize: 12,
    }))
  );

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get("/api/templates/invoice"); // Fallback check
        const vouchRes = await axios.get("/api/templates/voucher");
        const refRes = await axios.get("/api/templates/refund");
        
        const allTemplates = [...res.data, ...vouchRes.data, ...refRes.data];
        const found = allTemplates.find((t) => t.id === parseInt(id));
        
        if (found) {
          const config = found.config || {};
          setFormData({
            name: found.name || "",
            category: found.category || "",
            is_active: found.is_active || false,
            title: config.title || "TAX INVOICE",
            defaultFont: config.defaultFont || "Verdana",
            defaultFontColor: config.defaultFontColor || "#0b2c4d",
            defaultFontSize: config.defaultFontSize || 12,
            headerText: config.headerText || "",
            detailsText1: config.detailsText1 || "",
            detailsText2: config.detailsText2 || "",
            termsAndConditions: config.termsAndConditions || "",
            addStamp: config.addStamp !== undefined ? config.addStamp : true,
            addSignature: config.addSignature !== undefined ? config.addSignature : true,
            addBankDetails: config.addBankDetails !== undefined ? config.addBankDetails : true,
            logoUrl: config.logoUrl || "",
            stampUrl: config.stampUrl || "",
            signatureUrl: config.signatureUrl || "",
          });
          if (config.areaStyles) {
             setAreaStyles(config.areaStyles);
          }
        }
      } catch (error) {
        console.error("Error fetching template", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [id]);

  const handleAreaChange = (index, field, value) => {
    const updated = [...areaStyles];
    updated[index][field] = value;
    setAreaStyles(updated);
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);

    try {
      const res = await axios.post("/api/templates/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, [field]: res.data.url }));
      alert("File uploaded successfully!");
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Upload failed.");
    }
  };

  const handleSave = async () => {
    const configData = {
      title: formData.title,
      defaultFont: formData.defaultFont,
      defaultFontColor: formData.defaultFontColor,
      defaultFontSize: formData.defaultFontSize,
      headerText: formData.headerText,
      detailsText1: formData.detailsText1,
      detailsText2: formData.detailsText2,
      termsAndConditions: formData.termsAndConditions,
      addStamp: formData.addStamp,
      addSignature: formData.addSignature,
      addBankDetails: formData.addBankDetails,
      logoUrl: formData.logoUrl,
      stampUrl: formData.stampUrl,
      signatureUrl: formData.signatureUrl,
      areaStyles,
    };

    const payload = {
      name: formData.name,
      category: formData.category,
      is_active: formData.is_active,
      config: configData,
    };

    try {
      await axios.put(`/api/templates/${id}`, payload);
      alert("Template saved successfully!");
      navigate("/dashboard/templates");
    } catch (error) {
      console.error("Error saving template", error);
      alert("Error saving template.");
    }
  };

  const handleActivate = async () => {
    try {
      await axios.put(`/api/templates/activate/${id}`);
      setFormData((prev) => ({ ...prev, is_active: true }));
      alert("Template activated as default!");
    } catch (error) {
      console.error("Error activating template", error);
    }
  };

  if (loading) return <div className="p-5 text-center">Loading template settings...</div>;

  return (
    <div className="container-fluid py-4" style={{backgroundColor: "#f8f9fa"}}>
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h3 className="m-0 text-primary">Template Customization</h3>
          <small className="text-muted">Edit settings for <strong>{formData.name}</strong> ({formData.category})</small>
        </div>
        <div className="d-flex gap-2">
          {!formData.is_active && (
            <button className="btn btn-outline-warning" onClick={handleActivate}>
              Set as Active
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => navigate("/dashboard/templates")}>
            Back
          </button>
          <button className="btn btn-primary px-4" onClick={handleSave}>
            Save All Changes
          </button>
        </div>
      </div>

      <div className="row">
        {/* Left Column: Settings */}
        <div className="col-lg-8">
          
          {/* Global Styles */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-header bg-white py-3">
              <h5 className="m-0 fw-bold"><i className="bi bi-gear-fill me-2"></i>Global Typography & Branding</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold small text-uppercase">Document Title</label>
                  <input
                    className="form-control"
                    placeholder="e.g. TAX INVOICE, PAYMENT RECEIPT"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold small text-uppercase">Default Font Family</label>
                  <select
                    className="form-select"
                    value={formData.defaultFont}
                    onChange={(e) => setFormData({ ...formData, defaultFont: e.target.value })}
                  >
                    {fonts.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold small text-uppercase">Primary Font Color</label>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={formData.defaultFontColor}
                      onChange={(e) => setFormData({ ...formData, defaultFontColor: e.target.value })}
                    />
                    <span className="text-muted small">{formData.defaultFontColor}</span>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold small text-uppercase">Base Font Size (px)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.defaultFontSize}
                    onChange={(e) => setFormData({ ...formData, defaultFontSize: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Area Specific Styles */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-header bg-white py-3">
              <h5 className="m-0 fw-bold"><i className="bi bi-palette-fill me-2"></i>Area-Specific Styles</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">DOCUMENT AREA</th>
                      <th className="text-center">BACKGROUND</th>
                      <th className="text-center">FONT COLOR</th>
                      <th className="text-center">FONT SIZE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaStyles.map((item, index) => (
                      <tr key={index}>
                        <td className="ps-4 fw-semibold small text-muted">{item.area}</td>
                        <td className="text-center">
                          <input
                            type="color"
                            className="form-control-color border-0"
                            value={item.bgColor}
                            onChange={(e) => handleAreaChange(index, "bgColor", e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="color"
                            className="form-control-color border-0"
                            value={item.fontColor}
                            onChange={(e) => handleAreaChange(index, "fontColor", e.target.value)}
                          />
                        </td>
                        <td className="text-center" style={{width: 100}}>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={item.fontSize}
                            onChange={(e) => handleAreaChange(index, "fontSize", parseInt(e.target.value))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-header bg-white py-3">
              <h5 className="m-0 fw-bold"><i className="bi bi-type-h1 me-2"></i>Content & Terms</h5>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <label className="form-label fw-bold small text-uppercase text-primary">Header Greeting / Text</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Greeting at the top of the document..."
                  value={formData.headerText}
                  onChange={(e) => setFormData({ ...formData, headerText: e.target.value })}
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-4">
                  <label className="form-label fw-bold small text-uppercase text-primary">Main Details Text</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Text above the items table..."
                    value={formData.detailsText1}
                    onChange={(e) => setFormData({ ...formData, detailsText1: e.target.value })}
                  />
                </div>
                <div className="col-md-6 mb-4">
                  <label className="form-label fw-bold small text-uppercase text-primary">Secondary Details Text</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Text below the items table..."
                    value={formData.detailsText2}
                    onChange={(e) => setFormData({ ...formData, detailsText2: e.target.value })}
                  />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label fw-bold small text-uppercase text-primary">Terms & Conditions</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Legal text at the bottom..."
                  value={formData.termsAndConditions}
                  onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Assets & Toggles */}
        <div className="col-lg-4">
          
          {/* Asset Uploads */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-header bg-white py-3">
              <h5 className="m-0 fw-bold"><i className="bi bi-image me-2"></i>Visual Assets</h5>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <label className="form-label fw-bold small">Corporate Logo (PNG/JPG)</label>
                <input type="file" className="form-control mb-2" onChange={(e) => handleFileUpload(e, "logoUrl")} />
                {formData.logoUrl && (
                  <div className="border rounded p-2 text-center bg-light">
                    <img src={formData.logoUrl} alt="Logo" style={{maxHeight: 60}} />
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <label className="form-label fw-bold small">Authorized Stamp (PNG)</label>
                <input type="file" className="form-control mb-2" onChange={(e) => handleFileUpload(e, "stampUrl")} />
                {formData.stampUrl && (
                  <div className="border rounded p-2 text-center bg-light">
                    <img src={formData.stampUrl} alt="Stamp" style={{maxHeight: 60}} />
                  </div>
                )}
              </div>

              <div className="mb-2">
                <label className="form-label fw-bold small">Official Signature (PNG)</label>
                <input type="file" className="form-control mb-2" onChange={(e) => handleFileUpload(e, "signatureUrl")} />
                {formData.signatureUrl && (
                  <div className="border rounded p-2 text-center bg-light">
                    <img src={formData.signatureUrl} alt="Signature" style={{maxHeight: 60}} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Configuration Toggles */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-3">
              <h5 className="m-0 fw-bold"><i className="bi bi-toggle-on me-2"></i>Visibility Toggles</h5>
            </div>
            <div className="card-body">
              <div className="form-check form-switch mb-3 p-0 d-flex justify-content-between align-items-center">
                <label className="form-check-label fw-semibold">Show Authorized Stamp</label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  style={{float: 'none', margin: 0}}
                  checked={formData.addStamp}
                  onChange={(e) => setFormData({ ...formData, addStamp: e.target.checked })}
                />
              </div>
              <div className="form-check form-switch mb-3 p-0 d-flex justify-content-between align-items-center">
                <label className="form-check-label fw-semibold">Show Signature</label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  style={{float: 'none', margin: 0}}
                  checked={formData.addSignature}
                  onChange={(e) => setFormData({ ...formData, addSignature: e.target.checked })}
                />
              </div>
              <div className="form-check form-switch mb-0 p-0 d-flex justify-content-between align-items-center">
                <label className="form-check-label fw-semibold">Include Bank Details</label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  style={{float: 'none', margin: 0}}
                  checked={formData.addBankDetails}
                  onChange={(e) => setFormData({ ...formData, addBankDetails: e.target.checked })}
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── FULL-WIDTH INVOICE PREVIEW ── */}
      <div className="card shadow-sm mt-4 border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
          <h5 className="m-0 fw-bold">
            <i className="bi bi-eye-fill me-2 text-primary"></i>Live Invoice Preview
          </h5>
          <small className="text-muted fst-italic">Updates as you change settings above</small>
        </div>
        <div className="card-body p-0" style={{ background: "#d0d5dd" }}>
          <div style={{ padding: "30px", display: "flex", justifyContent: "center" }}>
            <div
              style={{
                background: "#fff",
                width: "210mm",
                minHeight: "297mm",
                padding: "15mm",
                boxSizing: "border-box",
                boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                fontFamily: formData.defaultFont || "Arial",
                color: formData.defaultFontColor || "#0b2c4d",
                fontSize: `${formData.defaultFontSize || 12}px`,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* HEADER: Logo + QR/Meta */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  {formData.logoUrl ? (
                    <img src={formData.logoUrl} alt="Logo" style={{ maxHeight: 70, maxWidth: 160 }} crossOrigin="anonymous" />
                  ) : (
                    <div style={{ width: 150, height: 65, background: "#f3f4f6", border: "2px dashed #cbd5e1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#94a3b8", borderRadius: 4 }}>
                      <i className="bi bi-image" style={{ fontSize: 20, marginBottom: 4 }}></i>
                      Upload Logo Above
                    </div>
                  )}
                </div>

                <div style={{ textAlign: "right", fontSize: 10, lineHeight: 1.6 }}>
                  <div style={{ width: 70, height: 70, border: "1px solid #e2e8f0", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=70x70&data=PREVIEW-INVOICE" alt="QR" />
                  </div>
                  <p style={{ margin: 0, fontWeight: "bold", color: "#800000" }}>INVOICE#: IV-PREVIEW</p>
                  <p style={{ margin: 0 }}>TRN#: 100519322000003</p>
                  <p style={{ margin: 0 }}>STAFF#: Admin</p>
                </div>
              </div>

              {/* GREETING */}
              {formData.headerText && (
                <div style={{ marginBottom: 12, fontSize: 11, color: "#1e3a5f", fontStyle: "italic", whiteSpace: "pre-line" }}>
                  {formData.headerText}
                </div>
              )}

              {/* TITLE BAR */}
              <div style={{ textAlign: "center", borderTop: "2px solid #002040", borderBottom: "2px solid #002040", padding: "6px 0", marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 1 }}>
                  {formData.title || "TAX INVOICE"} - الفاتورة الضريبية
                </h2>
              </div>

              {/* CUSTOMER + SUMMARY BOX */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: "bold", fontSize: 12, margin: 0 }}>Valued Customer</p>
                  <p style={{ fontSize: 10, color: "#555", margin: 0 }}>SHARJAH, UAE</p>
                  <p style={{ fontSize: 10, color: "#555", margin: 0 }}>055-XXXXXXX</p>
                  <p style={{ fontSize: 10, color: "#555", margin: 0 }}>ACC#: CS-B1-001</p>
                </div>
                <table style={{ borderCollapse: "collapse", fontSize: 10, width: "45%" }}>
                  <thead>
                    <tr style={{ background: "#002040", color: "#fff" }}>
                      <th style={{ border: "1px solid #002040", padding: "4px 8px" }}>INVOICE DATE</th>
                      <th style={{ border: "1px solid #002040", padding: "4px 8px", textAlign: "center" }}>AMOUNT</th>
                      <th style={{ border: "1px solid #002040", padding: "4px 8px" }}>DUE DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ textAlign: "center", height: 36 }}>
                      <td style={{ border: "1px solid #002040", padding: "4px 8px" }}>{new Date().toLocaleDateString()}</td>
                      <td style={{ border: "1px solid #002040", padding: "4px 8px", background: "#002040", color: "#fff", fontWeight: "bold", fontSize: 13 }}>AED 1,050.00</td>
                      <td style={{ border: "1px solid #002040", padding: "4px 8px" }}></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* DETAILS TEXT */}
              {formData.detailsText1 && (
                <div style={{ fontSize: 10, color: "#1e3a5f", fontStyle: "italic", marginBottom: 10, whiteSpace: "pre-line" }}>
                  {formData.detailsText1}
                </div>
              )}

              {/* SERVICES TABLE */}
              <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                {/* Watermark logo */}
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.07, pointerEvents: "none" }}>
                  {formData.logoUrl && <img src={formData.logoUrl} alt="" style={{ width: "55%" }} crossOrigin="anonymous" />}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, position: "relative", zIndex: 1 }}>
                  <thead style={{ background: "#002040", color: "#fff", textTransform: "uppercase" }}>
                    <tr>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "left" }}>DETAILS / التفاصيل</th>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "right", width: 80 }}>AMOUNT</th>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "right", width: 70 }}>DISCOUNT</th>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "right", width: 70 }}>VAT (5%)</th>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "center", width: 50 }}>QTY</th>
                      <th style={{ border: "1px solid #002040", padding: "6px 8px", textAlign: "right", width: 80 }}>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "Air Ticket Service", desc: "Dubai → London | Economy Class", amount: 1000, vat: 50, qty: 1, total: 1050 },
                      { name: "Hotel Booking", desc: "5-Night Stay — Marriott Dubai", amount: 1200, vat: 60, qty: 1, total: 1260 },
                    ].map((row, i) => (
                      <tr key={i} style={{ verticalAlign: "top" }}>
                        <td style={{ border: "1px solid #002040", padding: "8px", minHeight: 60 }}>
                          <p style={{ fontWeight: "bold", margin: 0 }}>Valued Customer</p>
                          <p style={{ fontWeight: "bold", margin: 0 }}>{row.name}</p>
                          <p style={{ color: "#666", fontStyle: "italic", margin: 0 }}>{row.desc}</p>
                        </td>
                        <td style={{ border: "1px solid #002040", padding: "8px", textAlign: "right" }}>{row.amount.toFixed(2)}</td>
                        <td style={{ border: "1px solid #002040", padding: "8px", textAlign: "right" }}>0.00</td>
                        <td style={{ border: "1px solid #002040", padding: "8px", textAlign: "right" }}>{row.vat.toFixed(2)}</td>
                        <td style={{ border: "1px solid #002040", padding: "8px", textAlign: "center" }}>1</td>
                        <td style={{ border: "1px solid #002040", padding: "8px", textAlign: "right", fontWeight: "bold" }}>{row.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* BANK DETAILS */}
              {formData.addBankDetails && (
                <div style={{ marginTop: 24 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: "#002040", color: "#fff" }}>
                        <th colSpan={2} style={{ border: "1px solid #002040", padding: "5px 8px", textAlign: "center" }}>
                          BANK ACCOUNT INFORMATION - بيانات الحساب البنكي
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[["Account Title","REKONIC TRAVEL LLC"],["Account Number","010XXXXXXXXXX"],["Bank Name","MASHREQ BANK"],["IBAN","AE07023XXXXXXXXXXXXXXX"]].map(([label, val]) => (
                        <tr key={label}>
                          <td style={{ border: "1px solid #002040", padding: "4px 8px", background: "#f8fafc", width: "28%" }}>{label}</td>
                          <td style={{ border: "1px solid #002040", padding: "4px 8px", fontWeight: "bold", textTransform: "uppercase" }}>{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* SIGNATURE + STAMP ROW */}
              {(formData.addSignature || formData.addStamp) && (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", gap: 32, marginTop: 24 }}>
                  {formData.addSignature && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ width: 130, height: 55, border: "1px solid #cbd5e1", marginBottom: 4 }}></div>
                      <p style={{ fontSize: 10, margin: 0, color: "#555" }}>Authorised Signature</p>
                    </div>
                  )}
                  {formData.addStamp && (
                    <div style={{ textAlign: "center" }}>
                      {formData.stampUrl ? (
                        <img src={formData.stampUrl} alt="Stamp" style={{ maxHeight: 90, maxWidth: 90, opacity: 0.85 }} crossOrigin="anonymous" />
                      ) : (
                        <div style={{ width: 90, height: 90, border: "2px dashed #cbd5e1", borderRadius: "50%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#94a3b8" }}>
                          <i className="bi bi-patch-check" style={{ fontSize: 18, marginBottom: 2 }}></i>
                          Upload Stamp
                        </div>
                      )}
                      <p style={{ fontSize: 10, margin: "4px 0 0", color: "#555" }}>Official Stamp</p>
                    </div>
                  )}
                </div>
              )}

              {/* FOOTER NOTE */}
              <div style={{ marginTop: 16, fontSize: 9, color: "#64748b", borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                <p style={{ margin: 0 }}>
                  {formData.termsAndConditions || "This is a \"Computer Generated Document\" and does not require any official signature."}
                </p>
              </div>

              {/* BOTTOM WATERMARK LOGO */}
              <div style={{ marginTop: "auto", paddingTop: 16, display: "flex", justifyContent: "center", opacity: 0.18 }}>
                {formData.logoUrl && <img src={formData.logoUrl} alt="" style={{ maxHeight: 35 }} crossOrigin="anonymous" />}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── END INVOICE PREVIEW ── */}

    </div>
  );
};

export default TemplateSettings;
